import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  KanbanBoardResponse,
  KanbanSortMode,
  TicketKanbanBoardAggregates,
  TicketKanbanCard,
  TicketKanbanQuery,
} from '@troia-v3/types';
import { useCallback, useState } from 'react';

import { ticketsService } from '../services/ticketsService';

interface UseTicketsKanbanParams {
  pipelineId: string | null;
  search?: string;
  filters?: TicketKanbanQuery['filters'];
  windowSize?: number;
  sortMode?: KanbanSortMode;
  /** Permite condicionar o fetch além do `!!pipelineId` (ex.: aba kanban não ativa). Default `true`. */
  enabled?: boolean;
}

type TicketKanbanBoard = KanbanBoardResponse<TicketKanbanCard, TicketKanbanBoardAggregates>;

/**
 * Board kanban de tickets com janelas por coluna (stage). Counts/busca vêm do
 * backend; `loadMore` anexa páginas por cursor; `applyCardMove`/`applyCardReorder`
 * fazem cirurgia de cache pós-move (sem refetch do board — preserva o scroll
 * das colunas). Espelho de `useLeadsKanban` (SP1/SP2) — Task 9 do SP3.
 *
 * Diferenças de `useLeadsKanban`: coluna = `stageId` (não `stepId`), sem
 * `budget`/`totalValue` monetário no card (tickets não têm valor — só `count`
 * é ajustado nas cirurgias de cache).
 */
export function useTicketsKanban({
  pipelineId,
  search,
  filters,
  windowSize = 50,
  sortMode = 'created',
  enabled = true,
}: UseTicketsKanbanParams) {
  const queryClient = useQueryClient();
  const queryKey = ['tickets-kanban', pipelineId, search ?? '', filters ?? {}, sortMode] as const;

  const query = useQuery({
    queryKey,
    queryFn: () =>
      ticketsService.getKanbanBoard({
        pipelineId: pipelineId as string,
        ...(search ? { search } : {}),
        ...(filters ? { filters } : {}),
        windowSize,
        sortMode,
      }),
    enabled: !!pipelineId && enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const [loadingColumns, setLoadingColumns] = useState<Record<string, boolean>>({});
  const [errorColumns, setErrorColumns] = useState<Record<string, boolean>>({});

  const loadMore = useCallback(
    async (columnId: string) => {
      const data = queryClient.getQueryData<TicketKanbanBoard>(queryKey);
      const column = data?.columns.find((c) => c.columnId === columnId);
      if (!column?.nextCursor || loadingColumns[columnId] || !pipelineId) return;

      setLoadingColumns((prev) => ({ ...prev, [columnId]: true }));
      setErrorColumns((prev) => ({ ...prev, [columnId]: false }));
      try {
        const page = await ticketsService.getKanbanColumn({
          pipelineId,
          stageId: columnId,
          cursor: column.nextCursor,
          ...(search ? { search } : {}),
          ...(filters ? { filters } : {}),
          windowSize,
          sortMode,
        });
        queryClient.setQueryData<TicketKanbanBoard>(queryKey, (old) =>
          old
            ? {
                ...old,
                columns: old.columns.map((c) => {
                  if (c.columnId !== columnId) return c;
                  // Dedupe por id: um card movido via applyCardMove (inserido no topo
                  // da coluna destino) já persiste no DB com o novo stageId antes desse
                  // fetch resolver — se o createdAt dele cair na janela da próxima
                  // página por cursor, o backend o retorna de novo e duplicaria o item.
                  const seen = new Set(c.items.map((i) => i.id));
                  return {
                    ...c,
                    items: [...c.items, ...page.items.filter((i) => !seen.has(i.id))],
                    nextCursor: page.nextCursor,
                    count: page.count,
                    totalValue: page.totalValue,
                  };
                }),
              }
            : old,
        );
      } catch (error) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status === 422) {
          // Cursor inválido/legado (formato de outro sortMode): re-tentar o
          // mesmo cursor é beco sem saída — limpa e refaz o board (espelho SP2 §6.1).
          queryClient.setQueryData<TicketKanbanBoard>(queryKey, (old) =>
            old
              ? { ...old, columns: old.columns.map((c) => (c.columnId === columnId ? { ...c, nextCursor: null } : c)) }
              : old,
          );
          void queryClient.invalidateQueries({ queryKey: ['tickets-kanban'] });
        } else {
          setErrorColumns((prev) => ({ ...prev, [columnId]: true }));
        }
      } finally {
        setLoadingColumns((prev) => ({ ...prev, [columnId]: false }));
      }
    },
    [queryClient, queryKey, pipelineId, search, filters, windowSize, sortMode, loadingColumns],
  );

  const applyCardMove = useCallback(
    (card: TicketKanbanCard, fromColumnId: string, toColumnId: string) => {
      queryClient.setQueryData<TicketKanbanBoard>(queryKey, (old) => {
        if (!old) return old;
        const movedCard: TicketKanbanCard = { ...card, stageId: toColumnId };
        return {
          ...old,
          columns: old.columns.map((c) => {
            if (c.columnId === fromColumnId) {
              return {
                ...c,
                items: c.items.filter((i) => i.id !== card.id),
                count: Math.max(0, c.count - 1),
              };
            }
            if (c.columnId === toColumnId) {
              return {
                ...c,
                items: [movedCard, ...c.items],
                count: c.count + 1,
              };
            }
            return c;
          }),
        };
      });
    },
    [queryClient, queryKey],
  );

  const applyCardReorder = useCallback(
    (card: TicketKanbanCard, fromColumnId: string, toColumnId: string, insertBeforeId: string | null = null) => {
      queryClient.setQueryData<TicketKanbanBoard>(queryKey, (old) => {
        if (!old) return old;
        const isCross = fromColumnId !== toColumnId;
        return {
          ...old,
          columns: old.columns.map((c) => {
            let items = c.items;
            let count = c.count;
            if (c.columnId === fromColumnId) {
              items = items.filter((i) => i.id !== card.id);
              if (isCross) {
                count = Math.max(0, count - 1);
              }
            }
            if (c.columnId === toColumnId) {
              const base = items.filter((i) => i.id !== card.id);
              const idx = insertBeforeId ? base.findIndex((i) => i.id === insertBeforeId) : -1;
              const at = idx === -1 ? base.length : idx;
              items = [...base.slice(0, at), card, ...base.slice(at)];
              if (isCross) {
                count += 1;
              }
            }
            return items === c.items ? c : { ...c, items, count };
          }),
        };
      });
    },
    [queryClient, queryKey],
  );

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['tickets-kanban'] });
  }, [queryClient]);

  return {
    boardData: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    loadMore,
    applyCardMove,
    applyCardReorder,
    invalidate,
    loadingColumns,
    errorColumns,
  };
}
